import React, { useRef } from "react";
import { StructuredText } from "react-datocms";
import { GatsbyImage } from "gatsby-plugin-image";
import PropTypes from "prop-types";
import Faq from "./Faq";
import { CallToAction } from "./CallToAction";
import Testimonials from "./Testimonials";

export const StructuredModuleTypes = {
  DatoCmsFaqItem: "DatoCmsFaqItem",
  DatoCmsImageBlock: "DatoCmsImageBlock",
  DatoCmsCtaBlock: "DatoCmsCtaBlock",
  DatoCmsScrolltocta: "DatoCmsScrolltocta",
  DatoCmsHtmlBlock: "DatoCmsHtmlBlock",
  DatoCmsTestimonialblock: "DatoCmsTestimonialblock",
};

function endsWithNumber(str) {
  return !isNaN(str.slice(-1));
}

export const getUnit = (unit) => {
  if (!unit) return undefined;
  if (typeof unit === "string") {
    if (endsWithNumber(unit.trim())) return `${unit}px`;
    return unit.trim();
  }
  return unit;
};

const StructuredTextRenderer = ({ data, renderImage }) => {
  const ctaId = useRef("_" + Math.random().toString(36).substr(2, 9));

  return (
    <StructuredText
      data={data}
      renderBlock={({ record }) => {
        switch (record.__typename) {
          case StructuredModuleTypes.DatoCmsImageBlock:
            if (renderImage) {
              return renderImage(record);
            }

            return (
              <figure
                style={{
                  marginTop: record.edgeToEdge ? 0 : undefined,
                  marginBottom: record.edgeToEdge ? 0 : undefined,
                  paddingBottom: record.paddingBottom ? "3rem" : undefined,
                }}
              >
                <GatsbyImage
                  loading="lazy"
                  image={record.image.gatsbyImageData}
                  alt={record.image.alt}
                  className={
                    record.noVerticalMargin ? "radius-0" : "",
                    record.limitedWidth ? "limitedwidth" : "radius-0"
                  }
                  imgStyle={{
                    margin: 0,
                    objectFit: "contain",
                  }}
                  style={{
                    marginRight: "auto",
                    marginLeft: "auto",
                    maxWidth: getUnit(
                      record.image.customData?.width ||
                        record.image.customData?.Width ||
                        record.image.customData?.maxWidth ||
                        record.image.customData?.MaxWidth
                    ),
                    maxHeight: getUnit(
                      record.image.customData?.height ||
                        record.image.customData?.Height ||
                        record.image.customData?.maxHeight ||
                        record.image.customData?.MaxHeight
                    ),
                    marginTop: record.noVerticalMargin ? 0 : undefined,
                    marginBottom: record.noVerticalMargin ? 0 : undefined,
                  }}
                  imgClassName={"img"}
                />
                {record.caption && (
                  <figcaption
                    dangerouslySetInnerHTML={{
                      __html: record.caption,
                    }}
                  />
                )}
              </figure>
            );
          case StructuredModuleTypes.DatoCmsFaqItem:
            return <Faq question={record.question} answer={record.answer} />;
          case StructuredModuleTypes.DatoCmsCtaBlock:
            return (
              <CallToAction
                id={ctaId.current}
                badge={record.badge}
                buttonLink={record.buttonLink}
                buttonText={record.buttonText}
                content={record.ctaContent}
                image={record.image}
                title={record.title}
              />
            );
          case StructuredModuleTypes.DatoCmsHtmlBlock:
            return (
              <div>
                <div
                  className="html-block"
                  dangerouslySetInnerHTML={{
                    __html: record.htmlField,
                  }}
                />
                <div
                  className="html-caption"
                  dangerouslySetInnerHTML={{
                    __html: record.htmlCaption,
                  }}
                />
              </div>
            );
          case StructuredModuleTypes.DatoCmsScrolltocta:
            return (
              <blockquote>
                <p>
                  <a href={"#" + ctaId.current}>{record.content}</a>
                </p>
              </blockquote>
            );
          case StructuredModuleTypes.DatoCmsTestimonialblock:
            return (
              <Testimonials
                className="pt-0 pb-3"
                testimonials={[
                  {
                    content: record.content,
                    gatsbyImageData: record.avatar.gatsbyImageData,
                    logo: <img src={record.logo.url} alt="" />,
                  },
                ]}
              />
            );
          default:
            return null;
        }
      }}
    />
  );
};

StructuredTextRenderer.propTypes = {
  data: PropTypes.object.isRequired,
};

export default StructuredTextRenderer;
